import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  Drawer,
  Paper,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ApiService } from '../../services/api';

interface IndustryTag {
  id: number;
  name: string;
  status: number; // 1 = Active, 0 = Inactive
  type_: number | null;
  observations: string | null;
  newsletter: string | null; // e.g. "THC", "CBD / INV" - which weekly newsletters this tag's companies get
}

// Per the tagging manual: THC (cannabis), CBD (hemp), INV (investors/lawyers), MED (doctors/pharmacists).
// A tag can map to more than one, e.g. packaging/security get both THC and CBD.
const NEWSLETTER_OPTIONS = ['THC', 'CBD', 'INV', 'MED'];
const parseNewsletters = (value: string | null): string[] =>
  value ? value.split('/').map(s => s.trim()).filter(Boolean) : [];
const joinNewsletters = (values: string[]): string | null =>
  values.length ? values.join(' / ') : null;

const IndustryTagsSettings: React.FC = () => {
  const [tags, setTags] = useState<IndustryTag[]>([]);
  const [filteredTags, setFilteredTags] = useState<IndustryTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Detail Drawer state
  const [selectedTag, setSelectedTag] = useState<IndustryTag | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState(true);
  const [editType, setEditType] = useState<number | ''>('');
  const [editObservations, setEditObservations] = useState('');
  const [editNewsletter, setEditNewsletter] = useState<string[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);

  // Create Tag Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStatus, setNewStatus] = useState(true);
  const [newType, setNewType] = useState<number | ''>('');
  const [newObservations, setNewObservations] = useState('');
  const [newNewsletter, setNewNewsletter] = useState<string[]>([]);

  // Delete Confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tags, searchQuery, statusFilter]);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getIndustryTags(null, 1000, 'name', 'ASC', true);
      if (response && Array.isArray(response.data)) {
        setTags(response.data);
      } else {
        setTags([]);
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to fetch industry tags.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...tags];

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter(t => t.status === 1);
    } else if (statusFilter === 'inactive') {
      result = result.filter(t => t.status === 0);
    }

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        t => t.name.toLowerCase().includes(q) || 
        (t.observations && t.observations.toLowerCase().includes(q))
      );
    }

    setFilteredTags(result);
  };

  const handleOpenDrawer = (tag: IndustryTag) => {
    setSelectedTag(tag);
    setEditName(tag.name);
    setEditStatus(tag.status === 1);
    setEditType(tag.type_ !== null && tag.type_ !== undefined ? tag.type_ : '');
    setEditObservations(tag.observations || '');
    setEditNewsletter(parseNewsletters(tag.newsletter));
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTag(null);
  };

  const handleSaveChanges = async () => {
    if (!selectedTag) return;
    if (!editName.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    setSaveLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.updateIndustryTag(selectedTag.id, {
        name: editName.trim(),
        status: editStatus ? 1 : 0,
        type_: editType !== '' ? Number(editType) : null,
        observations: editObservations.trim() || null,
        newsletter: joinNewsletters(editNewsletter)
      });
      setSuccess(`Tag "${editName.trim()}" updated successfully`);
      loadTags();
      handleCloseDrawer();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to save tag changes.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newName.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    setSaveLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.createIndustryTag({
        name: newName.trim(),
        status: newStatus ? 1 : 0,
        type_: newType !== '' ? Number(newType) : null,
        observations: newObservations.trim() || null,
        newsletter: joinNewsletters(newNewsletter)
      });
      setSuccess(`Tag "${newName.trim()}" created successfully`);
      setAddDialogOpen(false);
      // Reset form
      setNewName('');
      setNewStatus(true);
      setNewType('');
      setNewObservations('');
      setNewNewsletter([]);
      loadTags();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to create tag.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTag) return;

    setSaveLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.deleteIndustryTag(selectedTag.id);
      setSuccess(`Tag "${selectedTag.name}" deleted successfully`);
      setDeleteConfirmOpen(false);
      handleCloseDrawer();
      loadTags();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete tag.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 3 }}>
      {/* Alert Messaging */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header controls card */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search tags & descriptions..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <FormControl size="small" variant="outlined" sx={{ minWidth: 140 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="inactive">Inactive Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item sx={{ display: 'flex', gap: 1.5 }}>
            <IconButton onClick={loadTags} size="medium" sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
                backgroundColor: '#2563eb',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                }
              }}
            >
              Add New Tag
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Tags Directory Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#2563eb' }} />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: '#f8fafc', flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 2 }}>
            TAXONOMY DIRECTORY ({filteredTags.length} active filters / {tags.length} total tags)
          </Typography>
          <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
            <Grid container spacing={1.5}>
              {filteredTags.map((tag) => {
                const isActive = tag.status === 1;
                return (
                  <Grid item key={tag.id}>
                    <Tooltip title={tag.observations ? tag.observations : "No description yet"} arrow placement="top">
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: isActive ? '#10b981' : '#94a3b8' 
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {tag.name}
                            </Typography>
                            {tag.newsletter && (
                              <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700 }}>
                                {tag.newsletter}
                              </Typography>
                            )}
                            {tag.observations && (
                              <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.5, opacity: 0.6 }} />
                            )}
                          </Box>
                        }
                        onClick={() => handleOpenDrawer(tag)}
                        sx={{
                          p: 1,
                          height: 'auto',
                          borderRadius: 2,
                          backgroundColor: isActive ? 'white' : '#f1f5f9',
                          border: '1px solid',
                          borderColor: isActive ? '#e2e8f0' : '#cbd5e1',
                          color: isActive ? '#1e293b' : '#64748b',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            transform: 'translateY(-1px)',
                            backgroundColor: isActive ? '#f8fafc' : '#e2e8f0',
                          }
                        }}
                      />
                    </Tooltip>
                  </Grid>
                );
              })}
              {filteredTags.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No tags match your active search filters.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      )}

      {/* sliding Edit/Details Side Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            boxShadow: '-10px 0px 30px rgba(0,0,0,0.1)'
          }
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Tag Inspector
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure parameters & AI definition guidelines
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} size="small" sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* Drawer Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1, overflowY: 'auto' }}>
          <TextField
            label="Tag Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            variant="outlined"
            placeholder="e.g. Growers Support"
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel>Type (Optional)</InputLabel>
            <Select
              value={editType}
              onChange={(e) => setEditType(e.target.value as number | '')}
              label="Type (Optional)"
            >
              <MenuItem value="">Default/General</MenuItem>
              <MenuItem value={1}>Special / Ancillary</MenuItem>
              <MenuItem value={0}>Core / Plant Touching</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Newsletter(s)</InputLabel>
            <Select
              multiple
              value={editNewsletter}
              onChange={(e) => setEditNewsletter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Newsletter(s)"
              renderValue={(selected) => (selected as string[]).join(' / ') || 'None selected'}
            >
              {NEWSLETTER_OPTIONS.map((nl) => (
                <MenuItem key={nl} value={nl}>{nl}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ p: 2.5, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editStatus}
                  onChange={(e) => setEditStatus(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Active and Available for Tagging
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Turn off to hide from selection menus and prompts
                  </Typography>
                </Box>
              }
            />
          </Box>

          <TextField
            label="AI Guideline / Description (Observations)"
            multiline
            rows={8}
            fullWidth
            value={editObservations}
            onChange={(e) => setEditObservations(e.target.value)}
            variant="outlined"
            placeholder="Write clear tagging definitions here. Gemini reads this description to accurately determine whether a company matches this industry tag."
            helperText="Used directly by the AI Grounding agent to classify matching websites."
          />
        </Box>

        <Divider />

        {/* Drawer Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              borderColor: '#fca5a5',
              color: '#ef4444',
              '&:hover': {
                backgroundColor: '#fef2f2',
                borderColor: '#ef4444'
              }
            }}
          >
            Delete
          </Button>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={handleCloseDrawer}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              disabled={saveLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                backgroundColor: '#2563eb',
                '&:hover': {
                  backgroundColor: '#1d4ed8'
                }
              }}
            >
              {saveLoading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Add New Tag Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pb: 1 }}>
          Create New Industry Tag
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Establish a new classification inside your database taxonomy.
          </Typography>

          <TextField
            label="Tag Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Hydroponics Distributor"
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel>Type (Optional)</InputLabel>
            <Select
              value={newType}
              onChange={(e) => setNewType(e.target.value as number | '')}
              label="Type (Optional)"
            >
              <MenuItem value="">Default/General</MenuItem>
              <MenuItem value={1}>Special / Ancillary</MenuItem>
              <MenuItem value={0}>Core / Plant Touching</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Newsletter(s)</InputLabel>
            <Select
              multiple
              value={newNewsletter}
              onChange={(e) => setNewNewsletter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Newsletter(s)"
              renderValue={(selected) => (selected as string[]).join(' / ') || 'None selected'}
            >
              {NEWSLETTER_OPTIONS.map((nl) => (
                <MenuItem key={nl} value={nl}>{nl}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={newStatus}
                  onChange={(e) => setNewStatus(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Enable Immediately
                  </Typography>
                </Box>
              }
            />
          </Box>

          <TextField
            label="AI Guideline / Description (Observations)"
            multiline
            rows={5}
            fullWidth
            value={newObservations}
            onChange={(e) => setNewObservations(e.target.value)}
            placeholder="Explain to Gemini what companies fit this tag. Example: 'Companies that manufacture and sell greenhouse and irrigation supplies to commercial growers.'"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTag}
            disabled={saveLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 3,
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            {saveLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tag Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Are you sure?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You are about to delete the industry tag <strong>"{selectedTag?.name}"</strong>. This will permanently remove it from the taxonomy of all companies currently matching it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: 'none', fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={saveLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 2.5,
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            {saveLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IndustryTagsSettings;
